import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    functional: false,
    assistant: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      functional: true,
      assistant: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const consent = {
      necessary: true,
      analytics: false,
      functional: false,
      assistant: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setShowBanner(false);
  };

  const handleTogglePreference = (key: string) => {
    if (key === 'necessary') return;
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key as keyof typeof prev]
    }));
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed bottom-4 left-4 z-50 bg-white shadow-lg rounded-lg border border-gray-200 max-w-sm"
        >
          <div className="p-4">
            {!showDetails ? (
              <div>
                <div className="flex items-start mb-3">
                  <i className="ri-cookie-line text-xl text-gray-600 mr-2 flex-shrink-0 mt-0.5"></i>
                  <div>
                    <p className="text-xs text-gray-700 leading-relaxed">
                      Nous utilisons des cookies pour améliorer votre expérience.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={handleRejectAll}
                    className="flex-1 px-3 py-1.5 text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Refuser
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 px-3 py-1.5 text-xs bg-teal-600 text-white rounded hover:bg-teal-700 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Accepter
                  </button>
                </div>
                
                <button
                  onClick={() => setShowDetails(true)}
                  className="text-xs text-gray-500 hover:text-teal-600 cursor-pointer mt-2 block"
                >
                  Personnaliser
                </button>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-bold text-gray-900">
                    Paramètres
                  </h3>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="text-gray-500 hover:text-gray-700 cursor-pointer"
                  >
                    <i className="ri-close-line text-lg"></i>
                  </button>
                </div>

                <div className="space-y-2 mb-3 max-h-64 overflow-y-auto">
                  {/* Necessary Cookies */}
                  <div className="bg-gray-50 rounded p-2">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="text-xs font-bold text-gray-900 mb-1">Nécessaires</h4>
                        <p className="text-xs text-gray-600">Essentiels au fonctionnement</p>
                      </div>
                      <div className="ml-2">
                        <div className="w-8 h-4 bg-teal-600 rounded-full flex items-center justify-end px-0.5 cursor-not-allowed">
                          <div className="w-3 h-3 bg-white rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="bg-gray-50 rounded p-2">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="text-xs font-bold text-gray-900 mb-1">Analytiques</h4>
                        <p className="text-xs text-gray-600">Statistiques de visite</p>
                      </div>
                      <div className="ml-2">
                        <button
                          onClick={() => handleTogglePreference('analytics')}
                          className={`w-8 h-4 rounded-full flex items-center transition-all cursor-pointer ${
                            preferences.analytics ? 'bg-teal-600 justify-end' : 'bg-gray-300 justify-start'
                          } px-0.5`}
                        >
                          <div className="w-3 h-3 bg-white rounded-full"></div>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Functional Cookies */}
                  <div className="bg-gray-50 rounded p-2">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="text-xs font-bold text-gray-900 mb-1">Fonctionnels</h4>
                        <p className="text-xs text-gray-600">Mémorise vos préférences</p>
                      </div>
                      <div className="ml-2">
                        <button
                          onClick={() => handleTogglePreference('functional')}
                          className={`w-8 h-4 rounded-full flex items-center transition-all cursor-pointer ${
                            preferences.functional ? 'bg-teal-600 justify-end' : 'bg-gray-300 justify-start'
                          } px-0.5`}
                        >
                          <div className="w-3 h-3 bg-white rounded-full"></div>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Assistant Cookies */}
                  <div className="bg-gray-50 rounded p-2">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="text-xs font-bold text-gray-900 mb-1">Assistant IA</h4>
                        <p className="text-xs text-gray-600">Chat et vocal</p>
                      </div>
                      <div className="ml-2">
                        <button
                          onClick={() => handleTogglePreference('assistant')}
                          className={`w-8 h-4 rounded-full flex items-center transition-all cursor-pointer ${
                            preferences.assistant ? 'bg-teal-600 justify-end' : 'bg-gray-300 justify-start'
                          } px-0.5`}
                        >
                          <div className="w-3 h-3 bg-white rounded-full"></div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleRejectAll}
                    className="flex-1 px-3 py-1.5 text-xs border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Refuser
                  </button>
                  <button
                    onClick={handleSavePreferences}
                    className="flex-1 px-3 py-1.5 text-xs bg-teal-600 text-white rounded hover:bg-teal-700 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Enregistrer
                  </button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
